import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Checkbox } from '../../ui/checkbox';
import { supabase } from '../../../lib/supabase';
import { useToast } from '../../ui/use-toast';
import { Loader2, Lock, Eye, EyeOff } from 'lucide-react';
import { updateContestPermissions, removeContestPermissions } from '../../../utils/permissionSync';
import { useDomainQuery } from '../../../hooks/useDomainQuery';
import { updateUserPassword } from '../../../services/adminAuthService';

interface Contest {
  id: string;
  name: string;
  end_date: string;
  status: string;
}

interface Client {
  id: string;
  name: string;
  email: string;
  permissions: {
    features: string[];
    contests: string[];
  };
  is_active?: boolean;
}

interface ClientModalProps {
  isOpen: boolean;
  onClose: (refresh?: boolean) => void;
  client: Client | null;
}

const DEFAULT_FEATURES = [
  { id: 'view_stats', label: 'Voir les statistiques' },
  { id: 'manage_contestants', label: 'Gérer les candidats' },
  { id: 'view_transactions', label: 'Voir les transactions' }
];

export const ClientModal = ({ isOpen, onClose, client }: ClientModalProps) => {
  const { applyDomainFilter, getFilterParams } = useDomainQuery();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    selectedFeatures: [] as string[],
    selectedContests: [] as string[],
    is_active: true
  });
  
  const [contests, setContests] = useState<Contest[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingContests, setIsLoadingContests] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const fetchContests = async () => {
      try {
        setIsLoadingContests(true);
        let query = supabase
          .from('contests')
          .select('id, name, end_date, status')
          .order('name');
        
        query = applyDomainFilter(query);
        const { data, error } = await query;

        if (error) throw error;
        // Exclure les concours terminés (status = "finished" OU end_date < aujourd'hui)
        const now = new Date();
        const filtered = (data || []).filter((c: any) => {
          if (c.status && c.status.toLowerCase() === 'finished') return false;
          if (c.end_date && new Date(c.end_date) < now) return false;
          return true;
        });
        setContests(filtered);
      } catch (error) {
        console.error('Erreur lors du chargement des concours:', error);
        toast({
          variant: 'destructive',
          title: 'Erreur',
          description: 'Impossible de charger la liste des concours'
        });
      } finally {
        setIsLoadingContests(false);
      }
    };

    if (isOpen) {
      fetchContests();
    }
  }, [isOpen]);

  useEffect(() => {
    if (client) {
      setFormData({
        name: client.name || '',
        email: client.email || '',
        password: '',
        confirmPassword: '',
        selectedFeatures: client.permissions?.features || [],
        selectedContests: client.permissions?.contests || [],
        is_active: client.is_active ?? true
      });
    } else {
      setFormData({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        selectedFeatures: [],
        selectedContests: [],
        is_active: true
      });
    }
  }, [client]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Validation du mot de passe pour création
      if (!client) {
        if (!formData.password || formData.password.length < 6) {
          toast({
            title: 'Erreur',
            description: 'Le mot de passe doit contenir au moins 6 caractères',
            variant: 'destructive'
          });
          setIsLoading(false);
          return;
        }

        if (formData.password !== formData.confirmPassword) {
          toast({
            title: 'Erreur',
            description: 'Les mots de passe ne correspondent pas',
            variant: 'destructive'
          });
          setIsLoading(false);
          return;
        }
      }

      // Validation du mot de passe pour modification (si fourni)
      if (client && formData.password) {
        if (formData.password.length < 6) {
          toast({
            title: 'Erreur',
            description: 'Le mot de passe doit contenir au moins 6 caractères',
            variant: 'destructive'
          });
          setIsLoading(false);
          return;
        }

        if (formData.password !== formData.confirmPassword) {
          toast({
            title: 'Erreur',
            description: 'Les mots de passe ne correspondent pas',
            variant: 'destructive'
          });
          setIsLoading(false);
          return;
        }
      }

      if (client) {
        // Mise à jour d'un client existant
        
        // Mettre à jour le mot de passe si fourni (via Edge Function)
        if (formData.password && formData.password.trim()) {
          console.log('🔐 Mise à jour du mot de passe via Edge Function...');
          
          const passwordResult = await updateUserPassword({
            userId: client.id,
            password: formData.password,
            email: formData.email,
            name: formData.name
          });

          if (!passwordResult.success) {
            console.error('Erreur lors de la mise à jour du mot de passe:', passwordResult.error);
            toast({
              title: 'Avertissement',
              description: 'Le client a été mis à jour mais le mot de passe n\'a pas pu être changé.',
              variant: 'default',
            });
          } else {
            console.log('✅ Mot de passe mis à jour avec succès');
          }
        }

        // Mettre à jour le profil client
        const { error: updateError } = await supabase
          .from('users')
          .update({
            name: formData.name,
            is_active: formData.is_active,
            role: 'client'
          })
          .eq('id', client.id);

        if (updateError) throw updateError;

        // Mettre à jour les permissions pour chaque concours sélectionné
        const updatePromises = formData.selectedContests.map(contestId =>
          updateContestPermissions(
            client.id,
            contestId,
            formData.selectedFeatures
          )
        );

        // Supprimer les permissions des concours désélectionnés
        const removedContests = (client.permissions?.contests || [])
          .filter(id => !formData.selectedContests.includes(id));

        const removePromises = removedContests.map(contestId =>
          removeContestPermissions(client.id, contestId)
        );

        await Promise.all([...updatePromises, ...removePromises]);

        toast({
          title: 'Succès',
          description: `Client mis à jour avec succès${formData.password ? ' (mot de passe inclus)' : ''}`,
        });
      } else {
        // Création d'un nouveau client avec compte auth
        
        // Sauvegarder la session admin actuelle
        const { data: { session: adminSession } } = await supabase.auth.getSession();
        console.log('💾 Sauvegarde de la session admin avant création utilisateur');
        
        const { domain, tenantId } = getFilterParams();
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email: formData.email,
          password: formData.password,
          options: {
            data: {
              name: formData.name,
              role: 'client',
              tenant_id: tenantId.toString(),
              domain: domain
            }
          }
        });

        if (authError) throw authError;

        // Restaurer immédiatement la session admin
        if (adminSession) {
          console.log('🔄 Restauration de la session admin');
          await supabase.auth.setSession({
            access_token: adminSession.access_token,
            refresh_token: adminSession.refresh_token
          });
        }

        if (authData.user) {
          // Mettre à jour les informations du client
          const { error: insertError } = await supabase
            .from('users')
            .update({
              name: formData.name,
              is_active: formData.is_active,
              role: 'client'
            })
            .eq('id', authData.user.id);

          if (insertError) throw insertError;

          // Ajouter les permissions pour chaque concours sélectionné
          const permissionPromises = formData.selectedContests.map(contestId =>
            updateContestPermissions(
              authData.user!.id,
              contestId,
              formData.selectedFeatures
            )
          );

          await Promise.all(permissionPromises);

          console.log('✅ Client créé avec succès !');

          toast({
            title: 'Succès',
            description: `Le client ${formData.name} a été créé avec succès. Il peut maintenant se connecter.`,
          });
        }
      }

      onClose(true);
    } catch (error) {
      console.error('Error:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Une erreur est survenue lors de l\'enregistrement du client',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFeatureToggle = (featureId: string) => {
    setFormData(prev => ({
      ...prev,
      selectedFeatures: prev.selectedFeatures.includes(featureId)
        ? prev.selectedFeatures.filter(id => id !== featureId)
        : [...prev.selectedFeatures, featureId]
    }));
  };

  const handleContestToggle = (contestId: string) => {
    setFormData(prev => ({
      ...prev,
      selectedContests: prev.selectedContests.includes(contestId)
        ? prev.selectedContests.filter(id => id !== contestId)
        : [...prev.selectedContests, contestId]
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => !isLoading && onClose()}>
      <DialogContent className="sm:max-w-[600px] bg-gray-900 text-white">
        <DialogHeader>
          <DialogTitle>
            {client ? 'Modifier le client' : 'Nouveau client'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="bg-white/5 border-white/10"
                required
              />
            </div>

            {!client && (
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="bg-white/5 border-white/10"
                  required
                />
              </div>
            )}

            {client && (
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  className="bg-white/5 border-white/10 opacity-50 cursor-not-allowed"
                  disabled
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="password">
                Mot de passe {!client && <span className="text-red-400">*</span>}
                {client && <span className="text-xs text-gray-500 ml-2">(laisser vide pour ne pas modifier)</span>}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  className="bg-white/5 border-white/10 pr-10"
                  placeholder="••••••••"
                  required={!client}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">
                Confirmer le mot de passe {!client && <span className="text-red-400">*</span>}
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  className="bg-white/5 border-white/10 pr-10"
                  placeholder="••••••••"
                  required={!client}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <Label>Concours accessibles</Label>
              {isLoadingContests ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin text-yellow-400" />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {contests.map((contest) => (
                    <div key={contest.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`contest-${contest.id}`}
                        checked={formData.selectedContests.includes(contest.id)}
                        onCheckedChange={() => handleContestToggle(contest.id)}
                      />
                      <Label
                        htmlFor={`contest-${contest.id}`}
                        className="text-sm text-gray-300"
                      >
                        {contest.name}
                      </Label>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-4">
              <Label>Permissions</Label>
              <div className="grid grid-cols-2 gap-4">
                {DEFAULT_FEATURES.map((feature) => (
                  <div key={feature.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`feature-${feature.id}`}
                      checked={formData.selectedFeatures.includes(feature.id)}
                      onCheckedChange={() => handleFeatureToggle(feature.id)}
                    />
                    <Label
                      htmlFor={`feature-${feature.id}`}
                      className="text-sm text-gray-300"
                    >
                      {feature.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => 
                  setFormData(prev => ({ ...prev, is_active: checked as boolean }))
                }
              />
              <Label htmlFor="is_active" className="text-sm text-gray-300">
                Compte actif
              </Label>
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onClose()}
              disabled={isLoading}
              className="border-white/10 hover:bg-white/5"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                'Enregistrer'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
